import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Clock, UserRound, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmt, todayStr } from "@/lib/dates";
import { PRIORITY, TASK_STATUS, can } from "@/lib/colors";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill, Empty } from "@/components/common";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TaskCard = ({ task, user, onEdit, onChanged }) => {
  const canEdit = can(user, "assign_tasks") || task.assigned_by_id === user.id;
  const canStatus = canEdit || task.assignee_id === user.id;
  const overdue = task.status !== "done" && !!task.due && task.due.slice(0, 10) < todayStr();
  const patch = async (status) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status });
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 border-l-4 border-l-amber-500 p-4 space-y-2.5 transition-shadow hover:shadow-md fade-up" data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className={`font-bold text-slate-800 leading-snug ${task.status === "done" ? "line-through opacity-60" : ""}`}>{task.title}</h3>
        <Pill cls={PRIORITY[task.priority].cls}>{PRIORITY[task.priority].label}</Pill>
      </div>
      {task.description && <p className="text-sm text-slate-600 line-clamp-3">{task.description}</p>}
      {task.project_name && <Pill cls="bg-violet-50 text-violet-800 border-violet-200" testid={`task-card-project-${task.id}`}><FolderKanban className="w-3 h-3" /> {task.project_name}</Pill>}
      <div className="text-xs text-slate-500 space-y-1">
        <div className={`flex items-center gap-1.5 ${overdue ? "text-rose-600 font-bold" : ""}`}><Clock className="w-3.5 h-3.5" /> {task.due ? `Due ${fmt(task.due, "EEE d MMM, HH:mm")}` : "No due date"}{overdue && " · overdue"}</div>
        <div className="flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5" /> {task.assignee_name} · assigned by {task.assigned_by_name}</div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        {canStatus ? (
          <Select value={task.status} onValueChange={patch}>
            <SelectTrigger className="h-8 w-36 rounded-full text-xs" data-testid={`task-card-status-${task.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(TASK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        ) : <Pill cls={TASK_STATUS[task.status].cls}>{TASK_STATUS[task.status].label}</Pill>}
        {canEdit && (
          <div className="flex">
            <Button size="icon" variant="ghost" onClick={() => onEdit(task)} data-testid={`task-card-edit-${task.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" onClick={remove} data-testid={`task-card-delete-${task.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Tasks() {
  const { user } = useAuth();
  const [scope, setScope] = useState("mine");
  const [tasks, setTasks] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState("all");

  const load = useCallback(async () => {
    const { data } = await api.get("/tasks", { params: { scope } });
    setTasks(data);
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  return (
    <div>
      <PageHeader title="My tasks" subtitle="Everything on your plate, who asked for it and when it's due.">
        <Button className="rounded-full" onClick={() => setDialog({ task: null })} data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1.5" /> New task</Button>
      </PageHeader>
      <div className="flex flex-wrap items-center gap-3 mb-5">
      <Tabs value={scope} onValueChange={setScope}>
        <TabsList className="rounded-full">
          <TabsTrigger value="mine" className="rounded-full" data-testid="tasks-scope-mine">Assigned to me</TabsTrigger>
          <TabsTrigger value="created" className="rounded-full" data-testid="tasks-scope-created">Created by me</TabsTrigger>
          {can(user, "view_team", "assign_tasks") && <TabsTrigger value="all" className="rounded-full" data-testid="tasks-scope-all">All team tasks</TabsTrigger>}
        </TabsList>
      </Tabs>
      <Select value={projectFilter} onValueChange={setProjectFilter}>
        <SelectTrigger className="w-52 rounded-full h-9" data-testid="tasks-project-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All projects/locations</SelectItem>
          <SelectItem value="none">None</SelectItem>
          {projects.filter((p) => !p.archived).map((p) => <SelectItem key={p.id} value={p.id}>{p.kind === "location" ? `Location · ${p.name}` : p.name}</SelectItem>)}
        </SelectContent>
      </Select>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Object.entries(TASK_STATUS).map(([key, s]) => {
          const list = tasks.filter((t) => t.status === key && (projectFilter === "all" || (projectFilter === "none" ? !t.project_id : t.project_id === projectFilter)));
          return (
            <section key={key} className="rounded-3xl bg-slate-100/60 border border-slate-200/70 p-3" data-testid={`task-column-${key}`}>
              <div className="flex items-center justify-between px-2 pb-3">
                <Pill cls={s.cls}>{s.label}</Pill>
                <span className="text-sm text-slate-500 font-bold">{list.length}</span>
              </div>
              <div className="space-y-3">
                {list.map((t) => <TaskCard key={t.id} task={t} user={user} onEdit={(task) => setDialog({ task })} onChanged={load} />)}
                {list.length === 0 && <Empty>No tasks here.</Empty>}
              </div>
            </section>
          );
        })}
      </div>
      <TaskDialog open={!!dialog} task={dialog?.task} onClose={() => setDialog(null)} onSaved={load} />
    </div>
  );
}
